import { Alert, AlertTitle, Link, Typography } from "@mui/material";
import * as React from "react";

import { useSystemUser, useCurrentOrg } from "@/providers/auth/hooks";

import { OrganizationTier } from "../domain/orgs/OrgRecord";

type PremiumOrgOnlyProps = {
  fallback?: React.ReactNode;
  children: React.ReactNode;
  overrideForSystemUsers?: boolean;
};

export const DefaultPremiumOrgOnlyFallback: React.FC = () => {
  return (
    <Alert severity="info" sx={{ maxWidth: 600 }}>
      <AlertTitle>Upgrade to Access</AlertTitle>
      <Typography variant="body2" paragraph>
        This feature is only available on the premium tier.
      </Typography>
      <Typography variant="body2">
        Visit our{" "}
        <Link href="https://www.roboto.ai/pricing" underline="hover">
          website
        </Link>{" "}
        to learn more about upgrading your organization.
      </Typography>
    </Alert>
  );
};

/**
 * Renders children only if the current user is viewing on behalf of a premium org.
 * @param enforceOverride When true (default), respects the system user view override setting.
 * When false, always shows content to system users regardless of override.
 */
export const PremiumOrgOnly: React.FC<PremiumOrgOnlyProps> = ({
  children,
  fallback = <DefaultPremiumOrgOnlyFallback />,
  overrideForSystemUsers = true,
}) => {
  const { isSystemUser } = useSystemUser();
  const currentOrg = useCurrentOrg();

  const hasSystemUserAccess = overrideForSystemUsers && isSystemUser;
  const hasOrgAccess = currentOrg?.tier === OrganizationTier.Premium;

  if (!hasSystemUserAccess && !hasOrgAccess) {
    return fallback;
  }

  return children;
};
